import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import * as moment from 'moment';
import {
  Addon,
  Card,
  CardMetadata,
  ChangeSubscriptionLimits,
  PackagePlan,
  Plan,
  PlanWithLimits,
  Subscription
} from '../shared/models/plans';
import { Balance, PaymentParams } from '../account/account.model';
import { PaymentService } from '../shared/services/payment.service';
import { UserOrPending } from '../shared/user/user';
import { AdminMsisdn } from '../shared/msisdn/msisdns';
import { CustomQueryEncoderHelper } from '../shared/CustomQueryEncoderHelper';

@Injectable({
  providedIn: 'root'
})
export class AccountSettingsService {
  constructor(public http: HttpClient, private paymentService: PaymentService) {}

  getNotificationSettings() {
    return this.http.get('/settings/notifications');
  }

  saveNotifications({ lowBalanceEnabled, lowBalanceThreshold, email }) {
    return this.http.post('/settings/notifications', {
      autoRechargeEnabled: true,
      lowBalanceEnabled,
      lowBalanceThreshold,
      email
    });
  }

  getAutoRechargeSettings() {
    return this.http.get('/finance/charges/recharge');
  }

  chargeImmediately({ amount }) {
    return this.http.post('/finance/charges', {
      amount
    });
  }

  saveAutoRecharge({ enabled, amount, threshold }) {
    return this.http.post('/finance/charges/recharge', {
      enabled,
      amount,
      threshold: 10
    });
  }

  getBalance(): Observable<{ balance: Balance }> {
    let params = new HttpParams({ encoder: new CustomQueryEncoderHelper() });
    params = params.set(
      'dateFrom',
      moment()
        .startOf('day')
        .format()
    );

    return this.http.get<{ balance: Balance }>('/finance/balance', {
      params
    });
  }

  getPackagePlans(): Observable<PackagePlan[]> {
    return this.http.get<PackagePlan[]>('/finance/plans/package');
  }

  getPlans(): Observable<Plan[]> {
    return this.http.get<Plan[]>('/finance/plans');
  }

  getAddons(): Observable<Addon[]> {
    return this.http.get<Addon[]>('/finance/addons');
  }

  getSubscription(): Observable<Subscription> {
    return this.http.get<Subscription>('/finance/license-subscription');
  }

  currentPlan(): Observable<PlanWithLimits> {
    return this.http.get<PlanWithLimits>('/finance/current-plan');
  }

  getChangeSubscriptionLimits(planId: string): Observable<ChangeSubscriptionLimits> {
    return this.http.get<ChangeSubscriptionLimits>(`/finance/plans/${planId}/limits`);
  }

  getCard(): Observable<Card> {
    return this.http.get<Card>('/finance/card');
  }

  getCardMetadata(): Observable<CardMetadata> {
    return this.http.get<CardMetadata>('/finance/card/metadata');
  }

  getMsisdnsSubscriptions(): Observable<AdminMsisdn[]> {
    return this.http.get<AdminMsisdn[]>('/msisdns/occupied');
  }

  getMsisdns(): any {
    return this.http.get<any>('/msisdns');
  }

  submitSubscription({ planId, quantity }) {
    const originState = 'my-account';
    this.paymentService.onSpinnerActivate(true);
    this.http
      .post('/finance/subscription/', {
        planId,
        quantity
      })
      .subscribe(
        (v: PaymentParams) => this.paymentService.handleSubscriptionPayment(v, originState),
        () => {
          this.paymentService.redirect(originState);
          this.paymentService.onSpinnerActivate(false);
        }
      );
  }

  changeCard() {
    const originState = 'a';
    this.http.post('/finance/card/', {}).subscribe(
      (v: PaymentParams) => this.paymentService.handleSubscriptionPayment(v, originState),
      () => {
        this.paymentService.redirect(originState);
        this.paymentService.onSpinnerActivate(false);
      }
    );
  }

  getAllButAdministrativeUsers(): Observable<UserOrPending[]> {
    return this.http.get<UserOrPending[]>('/users');
  }

  updateUsersLimit(data) {
    return this.http.put<any>('/users/update-statuses', data);
  }

  isDetachableNumber(id) {
    return this.http.get<any>(`/msisdns/${id}/can-be-detached`);
  }

  detachNumber(id) {
    return this.http.put<any>(`/msisdns/${id}/detach`, {});
  }
}
